import { useState, useCallback } from 'react';
import { TextField, Chip, Box, Tooltip, IconButton } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useCaseFieldData, useCaseFieldDispatch } from '../../../hooks/useCaseField';
import type { FieldMode } from '../../../hooks/useCaseField';

interface IcdCodesChipsProps {
  mode: FieldMode;
  error?: string;
}

const IcdCodesChips: React.FC<IcdCodesChipsProps> = ({ mode, error }) => {
  const data = useCaseFieldData(mode);
  const { dispatchUpdate, readOnly } = useCaseFieldDispatch(mode);
  const [input, setInput] = useState('');

  const addCode = useCallback((code: string) => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed || data.icdCodes.some(c => c.value === trimmed)) return;
    dispatchUpdate({ icdCodes: [...data.icdCodes, { value: trimmed }] });
    setInput('');
  }, [dispatchUpdate, data.icdCodes]);

  const removeCode = useCallback((value: string) => {
    dispatchUpdate({ icdCodes: data.icdCodes.filter(c => c.value !== value) });
  }, [dispatchUpdate, data.icdCodes]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <TextField
          label="ICD-10 Codes" required size="small" fullWidth
          placeholder={readOnly ? '' : 'Type code, press Enter...'}
          value={input}
          disabled={readOnly}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCode(input); } }}
          error={!!error} helperText={error}
        />
        {!readOnly && (
          <IconButton size="small" onClick={() => addCode(input)}><AddIcon fontSize="small" /></IconButton>
        )}
      </Box>
      {data.icdCodes.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {data.icdCodes.map(icd => (
            <Tooltip key={icd.value} title={icd.description ?? ''} disableHoverListener={!icd.description}>
              <Chip label={icd.description ? `${icd.value} — ${icd.description}` : icd.value}
                size="small" color="secondary" variant="outlined"
                onDelete={readOnly ? undefined : () => removeCode(icd.value)} />
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default IcdCodesChips;
